import type { AnalyticsProvider, PageViewPayload } from '../types';
import { EVENT_CODES, type AnalyticsEventCode } from '../eventCodes';
import type { User } from '../../../types/context.types';
import { analyticsUserId } from '../userId';
import { COOKIE_CONSENT_REQUIRED_FOR_ANALYTICS } from '../consentFeatureFlags';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Injects gtag shim + Consent Mode v2 defaults before gtag.js loads.
 * When strict consent is required, defaults are denied until accept + grantGaConsent().
 * Otherwise defaults are granted so analytics runs without a banner step.
 */
let gaScriptLoaded = false;

export function injectGaConsentDefaults(): void {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId || typeof document === 'undefined') return;

  const strict = COOKIE_CONSENT_REQUIRED_FOR_ANALYTICS;
  const consentPayload = strict
    ? [
        "  ad_storage:'denied',",
        "  analytics_storage:'denied',",
        "  ad_user_data:'denied',",
        "  ad_personalization:'denied',",
      ]
    : [
        "  ad_storage:'granted',",
        "  analytics_storage:'granted',",
        "  ad_user_data:'granted',",
        "  ad_personalization:'granted',",
      ];

  const shimScript = document.createElement('script');
  shimScript.textContent = [
    'window.dataLayer=window.dataLayer||[];',
    'function gtag(){dataLayer.push(arguments);}',
    "gtag('consent','default',{",
    ...consentPayload,
    '  wait_for_update:500',
    '});',
  ].join('');
  document.head.appendChild(shimScript);

  if (gaScriptLoaded) return;
  gaScriptLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

/** Updates Consent Mode v2 to granted (e.g. after user opts back into analytics). */
export function grantGaConsent(): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'update', {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
}

// ── Provider ──────────────────────────────────────────────────────────────────

let gaConfigured = false;
let currentUserId: string | undefined;

function getUserIdFromProperties(properties?: Record<string, unknown>): string | undefined {
  const p = properties ?? {};
  if (p.user_id != null && String(p.user_id) !== '') return String(p.user_id);
  const uidFromUser = analyticsUserId((p.user as User | null | undefined) ?? null);
  return uidFromUser ?? currentUserId;
}

function configureGa(userId?: string): void {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId || gaConfigured || typeof window === 'undefined' || !window.gtag) return;
  gaConfigured = true;
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    page_path: window.location.pathname,
    anonymize_ip: true,
    ...(userId ? { user_id: userId } : {}),
  });
}

function gtagPageView(url: string, userId?: string): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId) return;
  window.gtag('config', measurementId, {
    page_path: url,
    ...(userId ? { user_id: userId } : {}),
  });
}

export const googleAnalyticsProvider: AnalyticsProvider = {
  init() {
    configureGa(currentUserId);
  },

  identify(user) {
    const uid = analyticsUserId(user ?? null);
    currentUserId = uid;
    if (!uid || typeof window === 'undefined' || !window.gtag) return;
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (!measurementId) return;
    window.gtag('set', { user_id: uid });
    window.gtag('config', measurementId, { user_id: uid });
  },

  reset() {
    gaConfigured = false;
    currentUserId = undefined;
  },

  trackPageView(payload: PageViewPayload) {
    const uid = analyticsUserId(payload.user);
    if (uid) currentUserId = uid;
    gtagPageView(payload.pathname, uid);
  },

  track(code: AnalyticsEventCode, properties?: Record<string, unknown>) {
    if (typeof window === 'undefined' || !window.gtag) return;
    const p = properties ?? {};
    const uid = getUserIdFromProperties(p);

    switch (code) {
      case EVENT_CODES.SIGN_UP:
        window.gtag('event', 'sign_up', {
          event_category: 'user',
          event_label: String(p.method ?? ''),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.SIGN_IN:
        window.gtag('event', 'login', {
          event_category: 'user',
          event_label: String(p.method ?? ''),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.PURCHASE:
        // GA4 recommended ecommerce event — mark `purchase` as a conversion in GA4
        window.gtag('event', 'purchase', {
          ...(uid ? { user_id: uid } : {}),
          transaction_id: typeof p.transaction_id === 'string' ? p.transaction_id : undefined,
          value: typeof p.revenue === 'number' ? p.revenue : undefined,
          currency: typeof p.currency === 'string' ? p.currency : undefined,
        });
        break;

      case EVENT_CODES.CONVERSION:
        // Custom subscription conversion event — mark as secondary conversion in GA4 if desired.
        // Do NOT mark as primary if you already use `purchase`; that would double-count.
        window.gtag('event', 'subscription_purchase', {
          event_category: 'ecommerce',
          ...(uid ? { user_id: uid } : {}),
          value: typeof p.conversion_value === 'number' ? p.conversion_value : undefined,
          conversion_type: typeof p.conversion_type === 'string' ? p.conversion_type : undefined,
        });
        break;

      case EVENT_CODES.CTA_CLICK:
        window.gtag('event', 'cta_click', {
          event_category: String(p.category ?? 'engagement'),
          event_label: String(p.label ?? ''),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.PLAN_CREATED:
        window.gtag('event', 'plan_created', {
          event_category: String(p.category ?? 'engagement'),
          event_label: String(p.label ?? ''),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.CREATE_PLAN_FUNNEL:
        window.gtag('event', 'create_plan_funnel', {
          event_category: 'create_plan_funnel',
          event_label: String(p.step ?? ''),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.CREATE_PLAN_ERROR:
        window.gtag('event', 'create_plan_error', {
          event_category: 'create_plan_funnel',
          event_label: String(p.error_type ?? ''),
          ...(p.error_code != null ? { error_code: String(p.error_code) } : {}),
          ...(p.step != null ? { step: Number(p.step) } : {}),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.ASSESSMENT_START:
        window.gtag('event', 'assessment_start', {
          event_category: 'assessment',
          event_label: String(p.objective_id ?? ''),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.ASSESSMENT_COMPLETE:
        window.gtag('event', 'assessment_complete', {
          event_category: 'assessment',
          event_label: String(p.bucket ?? ''),
          value: typeof p.overall_score === 'number' ? p.overall_score : undefined,
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.ASSESSMENT_REPORT_VIEWED:
        window.gtag('event', 'assessment_report_viewed', {
          event_category: 'assessment',
          session_id: p.session_id,
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.ASSESSMENT_PDF_DOWNLOADED:
        window.gtag('event', 'assessment_pdf_downloaded', {
          event_category: 'assessment',
          session_id: p.session_id,
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.ASSESSMENT_SHARE_CREATED:
        window.gtag('event', 'assessment_share_created', {
          event_category: 'assessment',
          session_id: p.session_id,
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.ASSESSMENT_PLAN_CTA_CLICKED:
        window.gtag('event', 'assessment_plan_cta_clicked', {
          event_category: 'assessment',
          session_id: p.session_id,
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.PLATFORM_SWITCH:
        window.gtag('event', 'platform_switch', {
          event_category: 'cross_product',
          from: String(p.from ?? ''),
          to: String(p.to ?? ''),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.STUDIO_LAUNCH_STARTED:
        window.gtag('event', 'studio_launch_started', {
          event_category: 'cross_product',
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.STUDIO_LAUNCH_COMPLETED:
        window.gtag('event', 'studio_launch_completed', {
          event_category: 'cross_product',
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.STUDIO_LAUNCH_FAILED:
        window.gtag('event', 'studio_launch_failed', {
          event_category: 'cross_product',
          error_message: String(p.error_message ?? ''),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.ERROR:
        window.gtag('event', 'app_error', {
          event_category: 'error',
          error_type: String(p.error_type ?? ''),
          error_message: String(p.error_message ?? ''),
          ...(p.error_code != null ? { error_code: String(p.error_code) } : {}),
          ...(uid ? { user_id: uid } : {}),
        });
        break;

      case EVENT_CODES.PAGE_VIEW:
      default:
        break;
    }
  },
};
